<template>
  <div :class="['card', cardClass]">
    <div class="flex gap-3 justify-between mb-4 row-wrapper">
      <h3 class="page-header-2">{{ title }}</h3>
      <div
        v-if="!readonly"
        :class="[
          'flex gap-3',
          hasMaxSizeText
            ? 'flex-col items-end md:flex-row md:items-center w-6/12 md:w-auto'
            : 'flex-row md:items-center'
        ]"
      >
        <slot name="header-extra" />
        <div class="whitespace-nowrap">
          <slot name="upload-button" />
        </div>
      </div>
    </div>
    <div class="media-grid">
      <MediaCard
        v-for="(item, index) in items"
        :key="item.id"
        :thumbnail-url="getThumbnailUrl(item)"
        :thumbnail-processed="item.thumbnail_processed ?? true"
        :name="item.name"
        :created-at="item.created_at"
        :customer-visible="item.customer_visible"
        :caption="item.caption"
        :captionable="captionable"
        :media-type="mediaType"
        :eager="index < EAGER_THUMBNAIL_COUNT"
        :selectable="selectable && !readonly && selection != null"
        :selected="isItemSelected(item.id)"
        :readonly="readonly"
        :scope="item.scope"
        :job-id="item.job_id"
        class="max-w-[300px]"
        @click="openViewer(item)"
        @delete="handleDelete(item.id)"
        @toggle-visibility="handleVisibilityToggle(item)"
        @toggle-select="toggleItemSelect(item.id)"
        @edit-caption="openCaptionEditor(item)"
      />
    </div>
    <DeleteConfirmModal
      v-if="deleteConfirm && !readonly"
      :item-type="itemTypeLabel"
      @confirm="onConfirmDelete"
      @cancel="closeDeleteConfirm"
    />
    <slot name="progress-modal" />
    <slot
      name="viewer-modal"
      :item="viewerItem"
      :close="closeViewer"
      :open-mode="viewerOpenMode"
      :can-go-previous="canGoPrevious"
      :can-go-next="canGoNext"
      :go-previous="goToPrevious"
      :go-next="goToNext"
      :save-caption="saveCaptionForViewer"
      :delete-caption="deleteCaptionForViewer"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends GalleryItem">
/* eslint-disable no-undef */
import { computed, inject, onBeforeUnmount, onMounted } from "vue";

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue";
import MediaCard, { type MediaType } from "@/components/MediaCard.vue";
import {
  type GalleryItem,
  type UseGalleryActionsOptions,
  useGalleryActions
} from "@/composables/useGalleryActions";
import { MEDIA_SELECTION_KEY } from "@/composables/useMediaSelection";

// Eager-load the first row of thumbnails so whichever one is the LCP element is
// never deprioritized by lazy-loading; the rest load lazily as they scroll in.
// Sized to the widest common grid row (see .media-grid min column width).
const EAGER_THUMBNAIL_COUNT = 6;

const props = withDefaults(
  defineProps<{
    title: string;
    items: T[];
    jobId: number | null;
    // Owner overrides for non-job scopes (e.g. customer media). When unset the
    // gallery falls back to jobId, preserving job-media behavior.
    ownerId?: number | null;
    ownerParamKey?: string;
    mediaType: MediaType;
    itemTypeLabel: string;
    cardClass?: string;
    hasMaxSizeText?: boolean;
    selectable?: boolean;
    readonly?: boolean;
    getThumbnailUrl?: (item: T) => string;
    galleryOptions: Omit<
      UseGalleryActionsOptions<T>,
      "items" | "ownerId" | "ownerParamKey"
    >;
  }>(),
  {
    cardClass: "photo-gallery",
    hasMaxSizeText: false,
    selectable: false,
    readonly: false,
    ownerId: null,
    ownerParamKey: undefined,
    getThumbnailUrl: (item: T) =>
      (item as T & { thumbnail?: { url: string } }).thumbnail?.url || ""
  }
);

const emit = defineEmits<{
  "delete-success": [itemId: number];
}>();

// Selection is owned by MediaTab and shared across all three galleries, so a
// single "Select all" and bulk action span photos, videos, and documents.
const selection = inject(MEDIA_SELECTION_KEY, null);

function isItemSelected(id: number): boolean {
  return selection?.isSelected(props.mediaType, id) ?? false;
}

function toggleItemSelect(id: number) {
  selection?.toggle(props.mediaType, id);
}

onMounted(() => {
  selection?.registerSection({
    type: props.mediaType,
    getItems: () => props.items
  });
});

onBeforeUnmount(() => {
  selection?.unregisterSection(props.mediaType);
});

const {
  deleteId,
  deleteConfirm,
  handleDelete,
  closeDeleteConfirm,
  confirmDelete,
  handleVisibilityToggle,
  viewerItem,
  viewerOpenMode,
  openViewer,
  closeViewer,
  saveCaption,
  deleteCaption,
  canGoPrevious,
  canGoNext,
  goToPrevious,
  goToNext
} = useGalleryActions<T>({
  items: () => props.items,
  ownerId: () => props.ownerId ?? props.jobId,
  ownerParamKey: props.ownerParamKey,
  mediaType: props.mediaType,
  ...props.galleryOptions
});

// Captions are supported only for media types whose gallery wires a caption
// action (photos/videos); documents don't, so their cards show no pen or badge.
const captionable = computed(() => !!props.galleryOptions.captionAction);

// Open-mode rule (locked design): the pen jumps straight to the input only when
// there's nothing to show yet; an item that already has a caption opens in read
// mode so a stray pen click can't clobber it — the user clicks Edit to change it.
function openCaptionEditor(item: T) {
  const hasCaption = !!item.caption && String(item.caption).trim().length > 0;
  openViewer(item, hasCaption ? "read" : "edit");
}

// The rail lives inside the viewer, which always acts on the open item, so the
// slot's caption handlers target viewerItem rather than threading it back down.
function saveCaptionForViewer(caption: string) {
  if (viewerItem.value) saveCaption(viewerItem.value, caption);
}

function deleteCaptionForViewer() {
  if (viewerItem.value) deleteCaption(viewerItem.value);
}

async function onConfirmDelete() {
  const itemId = await confirmDelete();
  if (itemId !== null) {
    emit("delete-success", itemId);
  }
}

defineExpose({
  deleteId,
  deleteConfirm,
  viewerItem,
  viewerOpenMode,
  handleDelete,
  handleVisibilityToggle,
  closeDeleteConfirm,
  openViewer,
  closeViewer,
  saveCaptionForViewer,
  deleteCaptionForViewer,
  isItemSelected,
  toggleItemSelect
});
</script>

<style scoped>
.media-grid {
  @apply p-0 grid gap-x-4 gap-y-6;
  /* Responsive grid: minimum 150px per item, minimum 2 columns, items grow to
     fill space. The 8px in the calc is half of gap-x-4 so two columns still
     fit on the narrowest screens. */
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(150px, calc(50% - 8px)), 1fr)
  );
}
</style>
